import type { ReviewQueueRoute } from "routes/Customer/Waste/Manifests/useReviewQueueRoute";

import { useQuery } from "@apollo/client";
import { Alert, Box, CircularProgress } from "@mui/material";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { ManifestReviewDialog } from "routes/Customer/Waste/Manifests/ManifestReviewDialog";

import { TenantFeatureFlagsProvider } from "./TenantFeatureFlagsProvider";

/**
 * Whose manifest is open, asked of the document itself.
 *
 * The queue spans tenants, so the answer changes as the reviewer moves through
 * it and cannot be carried once for the batch. Deliberately its own small query
 * rather than a field on the dialog's: the flags have to be resolved before the
 * dialog mounts, because they decide which of its sections exist.
 */
const REVIEW_DOCUMENT_TENANT = gql(`
  query ReviewDocumentTenant($id: ID!) {
    document(id: $id) {
      id
      tenantId
    }
  }
`);

/**
 * The document review the URL is currently pointing at.
 *
 * Addressed rather than held in state: the batch and the place in it come from
 * the query string, so a refresh, a Back press and a pasted link all land on the
 * document the reviewer was actually on. The queue's other dialogs stay in
 * component state — they are single-shot lookups with no position to lose.
 *
 * Wrapped in the open document's own tenant because the queue is cross-tenant
 * and the review is flag-gated: resolved against the staff user's session tenant
 * instead, the flag-gated sections would render against the wrong org.
 */
export function QueuedDocumentReview({ queue }: { queue: ReviewQueueRoute }) {
  const { currentDocumentId, documentIds } = queue;

  const { data, error } = useQuery(REVIEW_DOCUMENT_TENANT, {
    variables: { id: currentDocumentId ?? "" },
    skip: currentDocumentId == null,
  });

  // The next document's tenant, fetched while the reviewer is still on this one.
  //
  // Working forward is the whole motion of a queue, and without this every step
  // into another org's document waits a round trip with nothing on screen —
  // which reads as the review having closed. Apollo normalizes by document id,
  // so the query above finds it already answered. Stepping back is free for the
  // same reason.
  const nextDocumentId =
    currentDocumentId == null ? undefined : documentIds[documentIds.indexOf(currentDocumentId) + 1];
  useQuery(REVIEW_DOCUMENT_TENANT, {
    variables: { id: nextDocumentId ?? "" },
    skip: nextDocumentId == null,
  });

  // Only once the lookup has answered for *this* document. Apollo clears `data`
  // when the variables change, so an unguarded read hands the document being
  // entered whatever the document being left resolved to — and the mismatch is
  // invisible, because a tenant id is a tenant id.
  const answered = data?.document?.id === currentDocumentId;
  // The opening hint covers the document the batch was entered at, where the
  // lookup has not come back yet. `navigate` drops it, so it can never describe
  // a document the reviewer has since stepped off.
  const tenantId = answered ? data?.document?.tenantId : queue.tenantId;

  // Nothing is open, so there is nothing to say.
  if (currentDocumentId == null) return null;

  // Whose manifest this is decides which sections the review has, so it cannot
  // be rendered against a guess. Everything below stands in for the dialog until
  // that is known — silently rendering nothing reads as the review refusing to
  // open, which is what a reviewer reports rather than the lookup behind it.
  if (tenantId == null) {
    return (
      <Dialog
        open
        onClose={queue.close}
        noPrimaryAction
        title="Document review"
        aria-label="Document review"
      >
        {error == null ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress aria-label="Opening document review" />
          </Box>
        ) : (
          <Alert severity="error">
            This document could not be opened for review — we could not tell which organization it
            belongs to. Close and try again; if it keeps happening the document may not be linked to
            a tenant.
          </Alert>
        )}
      </Dialog>
    );
  }

  return (
    <TenantFeatureFlagsProvider tenantId={tenantId}>
      <ManifestReviewDialog
        documentIds={documentIds}
        currentDocumentId={currentDocumentId}
        onNavigate={queue.navigate}
        onClose={queue.close}
      />
    </TenantFeatureFlagsProvider>
  );
}
